import { useState, useRef, useEffect, useCallback } from 'react';
import DetailsSidebar from '../DetailsSidebar';
import DayContextMenu from '../DayContextMenu';
import EmptyStateHint from '../EmptyStateHint';
import Header from '../Header';
import CalendarGrid from './CalendarGrid';
import StickyOverlays from './StickyOverlays';
import CalendarModals from './CalendarModals';
import useCalendarModals from './useCalendarModals';
import { useCalendarDataContext } from '../../contexts/CalendarDataContext';
import { useSelectionContext } from '../../contexts/SelectionContext';
import { useSidebarContext } from '../../contexts/SidebarContext';
import { useDragContext } from '../../contexts/DragContext';
import { useContextMenuContext } from '../../contexts/ContextMenuContext';
import { useVisibleEntities } from '../../hooks/useVisibleEntities';
import useZoomPan from '../../hooks/useZoomPan';
import { toDateString } from '../../utils/dateUtils';
import { SIDEBAR_TYPE, SIDEBAR_MODE } from '../../constants/types';
import ZoomContext from '../../contexts/ZoomContext';
import './Calendar.scss';

/**
 * Main calendar component that orchestrates the entire calendar view.
 */
function Calendar({ initialYear, onShowInfo }) {
  const [year, setYear] = useState(initialYear);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const wrapperRef = useRef(null);
  
  // Detect Safari - getBoundingClientRect behaves differently with CSS zoom
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  // Zoom and pan state
  const { scaleFactor, isPanning, handleMouseDown } = useZoomPan(containerRef);
  
  // Year data and month heights (passed from CalendarGrid)
  const [yearData, setYearData] = useState([]);
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [monthHeights, setMonthHeights] = useState([]);

  // Get data from context
  const { 
    projects,
    events,
    milestones,
    holidays,
    isLoading,
    addEvent, 
    addMilestone,
    updateEvent,
    deleteEvent,
    updateMilestone,
    deleteMilestone,
    addHoliday,
    updateHoliday,
    deleteHoliday
  } = useCalendarDataContext();

  // Determine if calendar is empty (no events or milestones)
  const isEmpty = events.length === 0 && milestones.length === 0;
  const hasProjects = projects.length > 0;

  // Empty state hint dismissal state (session-only, resets on reload)
  const [emptyHintDismissed, setEmptyHintDismissed] = useState(false);

  const handleDismissEmptyHint = useCallback(() => {
    setEmptyHintDismissed(true);
  }, []);

  // Get UI state and handlers from individual contexts
  const { isSelecting, onDayMouseUp: handleDayMouseUp, clearSelection } = useSelectionContext();
  const { 
    sidebarType, 
    sidebarMode, 
    sidebarData,
    previewChanges,
    closeSidebar: closeSidebarOnly
  } = useSidebarContext();
  
  // Wrap closeSidebar to also clear selection
  const closeSidebar = useCallback(() => {
    closeSidebarOnly();
    clearSelection();
  }, [closeSidebarOnly, clearSelection]);
  const {
    dragInfo,
    dragInfoRef,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    findDayCellAtPoint
  } = useDragContext();
  const {
    showContextMenu,
    contextMenuDate,
    contextMenuPosition,
    setShowContextMenu,
    onAddEventFromMenu: handleAddEventFromMenu,
    onAddMilestoneFromMenu: handleAddMilestoneFromMenu
  } = useContextMenuContext();
  
  // Get computed visible entities with preview changes
  const { visibleEvents, visibleMilestones } = useVisibleEntities({
    sidebarMode,
    sidebarType,
    sidebarData,
    previewChanges
  });

  // Modal management
  const {
    showProjectForm,
    editingProject,
    showSettingsModal,
    showHolidayModal,
    showICalExportModal,
    handleAddProject,
    handleEditProject,
    handleSaveProject,
    handleCancelProject,
    handleDeleteProject,
    handleExport,
    handleImport,
    handleOpenSettings,
    handleCloseSettings,
    handleManageHolidays,
    handleOpenICalExport,
    handleCloseICalExport,
    handleICalExportConfirm,
    handleCloseHolidayModal,
    showCalendarManager,
    handleOpenCalendarManager,
    handleCloseCalendarManager,
    showICalSettings,
    handleOpenICalSettings,
    handleCloseICalSettings,
    showSharingPanel,
    handleOpenSharingPanel,
    handleCloseSharingPanel
  } = useCalendarModals();

  // Callbacks for CalendarGrid to pass data up
  const handleYearDataChange = useCallback((newYearData, newColumnHeaders) => {
    setYearData(newYearData);
    setColumnHeaders(newColumnHeaders);
  }, []);

  const handleHeightsChange = useCallback((heights) => {
    setMonthHeights(prev => {
      if (prev.length === heights.length && 
          prev.every((h, i) => Math.abs(h - heights[i]) < 0.1)) {
        return prev;
      }
      return heights;
    });
  }, []);

  // Year navigation
  const prevYear = () => setYear((y) => y - 1);
  const nextYear = () => setYear((y) => y + 1);
  const goToCurrentYear = () => setYear(new Date().getFullYear());

  // Handle sidebar save
  const handleSidebarSave = useCallback(async (formData) => {
    if (sidebarType === SIDEBAR_TYPE.EVENT) {
      if (sidebarMode === SIDEBAR_MODE.CREATE) {
        await addEvent(formData.title, formData.startDate, formData.endDate, formData.projectId, formData.description);
      } else {
        const projectChanged = formData.projectId !== sidebarData.projectId;
        await updateEvent(sidebarData.id, {
          title: formData.title,
          description: formData.description,
          projectId: formData.projectId,
          startDate: toDateString(formData.startDate),
          endDate: toDateString(formData.endDate),
          lockStart: formData.lockStart,
          lockEnd: formData.lockEnd,
          lockDuration: formData.lockDuration,
          color: projectChanged ? null : sidebarData.color
        });
      }
    } else {
      if (sidebarMode === SIDEBAR_MODE.CREATE) {
        await addMilestone(formData.title, formData.date, formData.icon, formData.projectId, null, formData.description);
      } else {
        await updateMilestone(sidebarData.id, {
          title: formData.title,
          description: formData.description,
          icon: formData.icon,
          projectId: formData.projectId,
          date: toDateString(formData.date),
          locked: formData.locked
        });
      }
    }
    
    closeSidebar();
  }, [sidebarType, sidebarMode, sidebarData, addEvent, updateEvent, addMilestone, updateMilestone, closeSidebar]);

  // Handle sidebar delete
  const handleSidebarDelete = useCallback((id) => {
    if (sidebarType === SIDEBAR_TYPE.EVENT) {
      deleteEvent(id);
    } else {
      deleteMilestone(id);
    }
    closeSidebar();
  }, [sidebarType, deleteEvent, deleteMilestone, closeSidebar]);

  // Global mouse up handler for selection
  useEffect(() => {
    if (isSelecting) {
      const handleGlobalMouseUp = () => {
        if (dragInfoRef.current) {
          return;
        }
        handleDayMouseUp();
      };
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isSelecting, handleDayMouseUp, dragInfoRef]);

  // Global listeners for real-time dragging
  // Depends on dragInfo state to re-run when drag starts/ends
  useEffect(() => {
    if (!dragInfo) return;
    
    const handleGlobalMouseMove = (e) => {
      handleDragMove(e);
    };
    
    const handleGlobalMouseUp = (e) => {
      const dayCell = findDayCellAtPoint(e.clientX, e.clientY);
      
      if (dayCell) {
        const dateStr = dayCell.getAttribute('data-date');
        if (dateStr) {
          const [y, m, d] = dateStr.split('-').map(Number);
          const targetDate = new Date(y, m - 1, d);
          targetDate.setHours(0, 0, 0, 0);
          handleDragEnd(targetDate);
          return;
        }
      }
      
      handleDragEnd(null);
    };
    
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [dragInfo, handleDragMove, handleDragEnd, findDayCellAtPoint]);

  // Update wrapper size when zoom or content changes
  // CSS zoom affects scrollWidth/scrollHeight, so they already include the zoom factor
  useEffect(() => {
    const content = contentRef.current;
    const wrapper = wrapperRef.current;
    if (!content || !wrapper) return;
    
    requestAnimationFrame(() => {
      wrapper.style.width = `${content.scrollWidth}px`;
      wrapper.style.height = `${content.scrollHeight}px`;
    });
  }, [scaleFactor, yearData]);

  return (
    <div className="calendar">
      <Header
        year={year}
        onPrevYear={prevYear}
        onNextYear={nextYear}
        onGoToCurrentYear={goToCurrentYear}
        onAddProject={handleAddProject}
        onEditProject={handleEditProject}
        onOpenSettings={handleOpenSettings}
        onShowInfo={onShowInfo}
        onManageCalendars={handleOpenCalendarManager}
        onShowSharing={handleOpenSharingPanel}
      />

      <div className="calendar__main">

      {/* Sticky overlays */}
      <StickyOverlays
        containerRef={containerRef}
        scaleFactor={scaleFactor}
        monthHeights={monthHeights}
        year={year}
        yearData={yearData}
        columnHeaders={columnHeaders}
        isDragging={!!dragInfo}
      />

      <div 
        className={`calendar__container ${isPanning ? 'calendar__container--panning' : ''}`}
        ref={containerRef}
        onMouseDown={handleMouseDown}
      >
        <div 
          className="calendar__wrapper"
          ref={wrapperRef}
        >
          <div
            className={`calendar__content ${dragInfo ? 'calendar__content--dragging' : ''}`}
            ref={contentRef}
            style={{ 
              zoom: scaleFactor,
              position: 'absolute',
              top: 0,
              left: 0
            }}
          >
            <ZoomContext.Provider value={{ scaleFactor, isSafari }}>
              <CalendarGrid
                year={year}
                scaleFactor={scaleFactor}
                visibleEvents={visibleEvents}
                visibleMilestones={visibleMilestones}
                onYearDataChange={handleYearDataChange}
                onHeightsChange={handleHeightsChange}
                isSafari={isSafari}
              />
            </ZoomContext.Provider>
          </div>

          {/* Empty state hint */}
          {isEmpty && !isLoading && !emptyHintDismissed && (
            <EmptyStateHint 
              hasProjects={hasProjects} 
              onDismiss={handleDismissEmptyHint}
            />
          )}
        </div>
      </div>

      {/* Day context menu */}
      {showContextMenu && (
        <DayContextMenu
          date={contextMenuDate}
          position={contextMenuPosition}
          onAddEvent={handleAddEventFromMenu}
          onAddMilestone={handleAddMilestoneFromMenu}
          onClose={() => setShowContextMenu(false)}
        />
      )}

      {/* All modals */}
      <CalendarModals
        showProjectForm={showProjectForm}
        editingProject={editingProject}
        projects={projects}
        onSaveProject={handleSaveProject}
        onCancelProject={handleCancelProject}
        onDeleteProject={handleDeleteProject}
        showSettingsModal={showSettingsModal}
        onCloseSettings={handleCloseSettings}
        onManageHolidays={handleManageHolidays}
        onExportJSON={handleExport}
        onExportICS={handleOpenICalExport}
        onImport={handleImport}
        showICalExportModal={showICalExportModal}
        onCloseICalExport={handleCloseICalExport}
        onICalExportConfirm={handleICalExportConfirm}
        showHolidayModal={showHolidayModal}
        holidays={holidays}
        onAddHoliday={addHoliday}
        onUpdateHoliday={updateHoliday}
        onDeleteHoliday={deleteHoliday}
        onCloseHolidayModal={handleCloseHolidayModal}
        showCalendarManager={showCalendarManager}
        onCloseCalendarManager={handleCloseCalendarManager}
        showICalSettings={showICalSettings}
        onCloseICalSettings={handleCloseICalSettings}
        onOpenICalSettings={handleOpenICalSettings}
        showSharingPanel={showSharingPanel}
        onCloseSharingPanel={handleCloseSharingPanel}
      />

      {/* Details sidebar */}
      <DetailsSidebar
        onSave={handleSidebarSave}
        onDelete={handleSidebarDelete}
      />
      </div>
    </div>
  );
}

export default Calendar;
